import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { get, post, put } from "@/services/ApiService";
import {
  CREATE_ORDER,
  GET_MY_ORDERS,
  GET_ORDER_BY_ID,
  UPLOAD_PAYMENT_PROOF,
  GET_PENDING_APPROVALS,
  APPROVE_ORDER_PAYMENT,
  REJECT_ORDER_PAYMENT,
  GET_PAYMENT_SETTINGS,
  TRACK_ORDER,
} from "@/services/ApiRoutes";

// ─── Types ────────────────────────────────────────────────────────────────────

export interface OrderItem {
  product: string;
  name: string;
  image: string;
  price: number;
  quantity: number;
  selectedSize?: string;
  selectedColor?: string;
  selectedVariant?: string;
}

export interface ShippingAddress {
  fullName: string;
  phone: string;
  // Backend expects "address" and "postalCode"
  address: string;
  postalCode: string;
  city: string;
  state?: string;
  country: string;
}

export interface Order {
  _id: string;
  orderNumber?: string;
  trackingNumber?: string;
  orderItems: OrderItem[];
  shippingAddress: ShippingAddress;
  paymentMethod: string;
  itemsPrice: number;
  taxPrice: number;
  shippingPrice: number;
  totalPrice: number;
  // Coupon info returned by backend as nested object
  coupon?: {
    code: string;
    discountType: string;
    discountValue: number;
    currency: string;
  };
  discountPrice?: number;           // server-computed discount amount
  orderNotes?: string;
  // Backend uses "orderStatus" (not "status")
  orderStatus: string;
  isPaid?: boolean;
  paidAt?: string;
  isDelivered?: boolean;
  deliveredAt?: string;
  paymentDeadline?: string;
  paymentProof?: {
    imageUrl?: string;              // backend field name
    transactionId?: string;
    notes?: string;
    uploadedAt?: string;            // backend field name
  };
  paymentApproval?: {
    status?: string;                // "Pending" | "Approved" | "Rejected"
    rejectionReason?: string;
    reviewedBy?: string;
    reviewedAt?: string;
  };
  createdAt: string;
  updatedAt: string;
}

export interface TrackingHistoryEntry {
  _id: string;
  status: string;
  description: string;
  updatedBy?: { _id: string; name: string };
  timestamp: string;
}

export interface TrackingData {
  _id: string;
  orderStatus: string;
  trackingNumber: string;
  trackingHistory: TrackingHistoryEntry[];
  estimatedDeliveryDate?: string;
  createdAt: string;
}

export interface PaymentMethodData {
  accountTitle: string;
  accountNumber: string;
  qrCodeImage?: string;
  bankName?: string;
  iban?: string;
  branchCode?: string;
}

export interface PaymentSettings {
  isJazzcashActive: boolean;
  isEasypaisaActive: boolean;
  isBankTransferActive: boolean;
  isCODActive: boolean;
  jazzcash: PaymentMethodData;
  easypaisa: PaymentMethodData;
  bankTransfer: PaymentMethodData;
  instructions: string;
  paymentDeadlineHours: number;
}

interface OrderState {
  orders: Order[];
  currentOrder: Order | null;
  pendingApprovals: Order[];
  paymentSettings: PaymentSettings | null;
  trackingData: TrackingData | null;
  trackingLoading: boolean;
  trackingError: string | null;
  loading: boolean;
  paymentSettingsLoading: boolean;
  proofLoading: boolean;
  approvalLoading: boolean;
  error: string | null;
  total: number;
  page: number;
  pages: number;
}

const initialState: OrderState = {
  orders: [],
  currentOrder: null,
  pendingApprovals: [],
  paymentSettings: null,
  trackingData: null,
  trackingLoading: false,
  trackingError: null,
  loading: false,
  paymentSettingsLoading: false,
  proofLoading: false,
  approvalLoading: false,
  error: null,
  total: 0,
  page: 1,
  pages: 1,
};

// ─── Thunks ───────────────────────────────────────────────────────────────────

export const fetchPaymentSettings = createAsyncThunk(
  "orders/fetchPaymentSettings",
  async (_, { rejectWithValue }) => {
    try {
      const res = await get(GET_PAYMENT_SETTINGS);
      return res.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || "Failed to load payment settings");
    }
  }
);

export const placeOrderAction = createAsyncThunk(
  "orders/placeOrder",
  async (
    orderData: {
      orderItems: OrderItem[];
      shippingAddress: ShippingAddress;
      paymentMethod: string;
      itemsPrice: number;
      taxPrice: number;
      shippingPrice: number;
      totalPrice: number;
      couponCode?: string;
      orderNotes?: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const res = await post(CREATE_ORDER, orderData);
      return res.data;
    } catch (err: any) {
      const data = err.response?.data;
      // Pass full error object so UI can show field-level errors
      return rejectWithValue({
        message: data?.message || "Failed to place order",
        errors: data?.errors || [],
      });
    }
  }
);

export const fetchMyOrders = createAsyncThunk(
  "orders/fetchMyOrders",
  async (_, { rejectWithValue }) => {
    try {
      const res = await get(GET_MY_ORDERS);
      return res;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || "Failed to fetch orders");
    }
  }
);

export const fetchOrderById = createAsyncThunk(
  "orders/fetchOrderById",
  async (id: string, { rejectWithValue }) => {
    try {
      const res = await get(GET_ORDER_BY_ID(id));
      return res.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || "Failed to fetch order");
    }
  }
);

export const uploadPaymentProofAction = createAsyncThunk(
  "orders/uploadPaymentProof",
  async (
    { orderId, formData }: { orderId: string; formData: FormData },
    { rejectWithValue }
  ) => {
    try {
      const res = await post(UPLOAD_PAYMENT_PROOF(orderId), formData);
      return res.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || "Failed to upload proof");
    }
  }
);

export const fetchPendingApprovals = createAsyncThunk(
  "orders/fetchPendingApprovals",
  async (limit: number = 20, { rejectWithValue }) => {
    try {
      const res = await get(`${GET_PENDING_APPROVALS}?limit=${limit}`);
      // Backend returns { success, message, data: [...], pagination }
      return res;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || "Failed to fetch pending approvals");
    }
  }
);

export const approvePaymentAction = createAsyncThunk(
  "orders/approvePayment",
  async (orderId: string, { rejectWithValue }) => {
    try {
      const res = await put(APPROVE_ORDER_PAYMENT(orderId), {});
      return { orderId, data: res.data };
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || "Failed to approve payment");
    }
  }
);

export const rejectPaymentAction = createAsyncThunk(
  "orders/rejectPayment",
  async (
    { orderId, rejectionReason }: { orderId: string; rejectionReason: string },
    { rejectWithValue }
  ) => {
    try {
      const res = await put(REJECT_ORDER_PAYMENT(orderId), { rejectionReason });
      return { orderId, data: res.data };
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || "Failed to reject payment");
    }
  }
);

export const trackOrderAction = createAsyncThunk(
  "orders/trackOrder",
  async (trackingNumber: string, { rejectWithValue }) => {
    try {
      const res = await get(TRACK_ORDER(trackingNumber));
      // Backend returns { success, message, data }
      return res.data?.data ?? res.data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.message || "Order not found. Please check your tracking number."
      );
    }
  }
);

// ─── Slice ────────────────────────────────────────────────────────────────────

const orderSlice = createSlice({
  name: "orders",
  initialState,
  reducers: {
    clearCurrentOrder: (state) => {
      state.currentOrder = null;
    },
    setCurrentOrder: (state, action: PayloadAction<Order>) => {
      state.currentOrder = action.payload;
    },
    clearOrderError: (state) => {
      state.error = null;
    },
    clearTrackingData: (state) => {
      state.trackingData = null;
      state.trackingError = null;
    },
  },
  extraReducers: (builder) => {
    // fetchPaymentSettings
    builder
      .addCase(fetchPaymentSettings.pending, (state) => {
        state.paymentSettingsLoading = true;
      })
      .addCase(fetchPaymentSettings.fulfilled, (state, action) => {
        state.paymentSettingsLoading = false;
        state.paymentSettings = action.payload;
      })
      .addCase(fetchPaymentSettings.rejected, (state) => {
        state.paymentSettingsLoading = false;
      });

    // placeOrder
    builder
      .addCase(placeOrderAction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(placeOrderAction.fulfilled, (state, action) => {
        state.loading = false;
        state.currentOrder = action.payload;
      })
      .addCase(placeOrderAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // fetchMyOrders — backend: { success, message, data: [...], pagination }
    builder
      .addCase(fetchMyOrders.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMyOrders.fulfilled, (state, action) => {
        state.loading = false;
        const payload = action.payload;
        // ApiService returns res.data (axios), so payload = { success, message, data, pagination }
        state.orders = Array.isArray(payload)
          ? payload
          : payload?.data || payload?.orders || [];
        state.total = payload?.pagination?.totalItems || state.orders.length;
        state.pages = payload?.pagination?.totalPages || 1;
      })
      .addCase(fetchMyOrders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // fetchOrderById
    builder
      .addCase(fetchOrderById.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchOrderById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentOrder = action.payload;
      })
      .addCase(fetchOrderById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // uploadPaymentProof
    builder
      .addCase(uploadPaymentProofAction.pending, (state) => {
        state.proofLoading = true;
        state.error = null;
      })
      .addCase(uploadPaymentProofAction.fulfilled, (state, action) => {
        state.proofLoading = false;
        state.currentOrder = action.payload;
      })
      .addCase(uploadPaymentProofAction.rejected, (state, action) => {
        state.proofLoading = false;
        state.error = action.payload as string;
      });

    // fetchPendingApprovals — backend: { success, message, data: [...], pagination }
    builder
      .addCase(fetchPendingApprovals.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchPendingApprovals.fulfilled, (state, action) => {
        state.loading = false;
        const payload = action.payload;
        state.pendingApprovals = Array.isArray(payload)
          ? payload
          : payload?.data || payload?.orders || [];
      })
      .addCase(fetchPendingApprovals.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // approvePayment
    builder
      .addCase(approvePaymentAction.pending, (state) => {
        state.approvalLoading = true;
      })
      .addCase(approvePaymentAction.fulfilled, (state, action) => {
        state.approvalLoading = false;
        state.pendingApprovals = state.pendingApprovals.filter(
          (o) => o._id !== action.payload.orderId
        );
      })
      .addCase(approvePaymentAction.rejected, (state, action) => {
        state.approvalLoading = false;
        state.error = action.payload as string;
      });

    // rejectPayment
    builder
      .addCase(rejectPaymentAction.pending, (state) => {
        state.approvalLoading = true;
      })
      .addCase(rejectPaymentAction.fulfilled, (state, action) => {
        state.approvalLoading = false;
        state.pendingApprovals = state.pendingApprovals.filter(
          (o) => o._id !== action.payload.orderId
        );
      })
      .addCase(rejectPaymentAction.rejected, (state, action) => {
        state.approvalLoading = false;
        state.error = action.payload as string;
      });

    // trackOrder
    builder
      .addCase(trackOrderAction.pending, (state) => {
        state.trackingLoading = true;
        state.trackingError = null;
        state.trackingData = null;
      })
      .addCase(trackOrderAction.fulfilled, (state, action) => {
        state.trackingLoading = false;
        state.trackingData = action.payload;
      })
      .addCase(trackOrderAction.rejected, (state, action) => {
        state.trackingLoading = false;
        state.trackingError = action.payload as string;
      });
  },
});

export const { clearCurrentOrder, setCurrentOrder, clearOrderError, clearTrackingData } = orderSlice.actions;
export default orderSlice.reducer;
